import { useCallback, useState } from 'react'
import { atom, useRecoilState, useSetRecoilState } from 'recoil'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { errorCheckApiClient as client } from '../utils/apiClient'
import type { components } from '../schema/error-check';
import { useSelectedOrCurrentYear } from './useYears';
import { useMe } from './useMe';
import { off } from 'process';

// -----------------------------------------------------------------------------
// Mocks to be removed
// -----------------------------------------------------------------------------
const _awarenessEpoch = new Date(2025, 9, 1, 0, 0, 0)
const _mockData: components["schemas"]["GetErroneousJianListResponse"] = {
  items: Array.from({ length: 37 }).map((_, i) => ({
    fireDepartmentId: "64f0a6ac-7aa5-400b-b34e-53b8667e9977",
    jianId: `uuid-jian-id-${i}`,
    dispatchInfoId: `uuid-dispatch-info-id-${i}`,
    teamId: `uuid-team-id-${i}`,
    awarenessDatetime: (new Date(_awarenessEpoch.getTime() + i * 24 * 3600 * 1000)).toISOString(),
    teamName: `出場隊名-${i}`,
    kakari: `警防第${(i % 3) + 1}係`,
    jianNumber: `事案番号-${i}`,
    accidentType: `事故種別-${i}`,
    dispatchLocationAddress: `出場場所住所-${i}`,
    isUnsent: `不搬送理由-${i % 2 == 0 ? 'あり' : 'なし'}`,
    jianSummary: `事案概要のサマリー情報がここに入ります。-${i}`,
    errorCount: Math.floor(Math.random() * 5),
    warningCount: i % 3 == 0 ? 0 : Math.floor(Math.random() * 7),
  })),
  nextToken: null,
};
function _getMockData(
  limit: number,
  warningCount: 0 | 1 | undefined,
  nextToken: string | undefined,
): components["schemas"]["GetErroneousJianListResponse"] {
  const token = nextToken ? parseInt(nextToken, 10) : NaN;
  const offset = isNaN(token) ? 0 : token;
  const next = offset + limit;
  const data = {..._mockData};
  if (!warningCount) {
    data.items = data.items.filter((item) => item.warningCount == 0);
  }
  const total = data.items.length;
  data.items = data.items.slice(offset, next);
  if (next < total) {
    data.nextToken = `${next}`;
  }
  return data;
}

// -----------------------------------------------------------------------------
// CRUD Operation Functions
// -----------------------------------------------------------------------------
const getErrorJians = async (
  teamId: string,
  year: number,
  limit: number,
  monthFrom?: number | undefined,
  monthTo?: number | undefined,
  warningCount?: 0 | 1 | undefined,
  nextToken?: string | undefined,
) => {
  //console.log('getErrorJians', {teamId, year, limit, monthFrom, monthTo, warningCount, nextToken});
  // When using mock
  //await new Promise((resolve) => setTimeout(resolve, 500));
  //return _getMockData(limit, warningCount, nextToken);
  const res = await client.GET('/error-check/jians/errors', {
    params: {
      query: {
        teamId,
        year,
        limit,
        monthFrom,
        monthTo,
        warningCount,
        nextToken,
      }
    }
  });
  if (res.error) {
    throw new Error(`Failed to GET /error-check/jians/errors \n message: ${res.error.message}`);
  }
  return res.data;
}

// -----------------------------------------------------------------------------
// Recoil States
// -----------------------------------------------------------------------------
const pageState = atom<number>({
  key: 'useErrorJian/pageState',
  default: 0,
});

const limitState = atom<number>({
  key: 'useErrorJian/limitState',
  default: 10,
});

const conditionState = atom<{
  monthFrom: number | null;
  monthTo: number | null;
  warningCount: 0 | 1;
}>({
  key: 'useErrorJian/conditionState',
  default: {
    monthFrom: null,
    monthTo: null,
    warningCount: 0,
  }
})

// -----------------------------------------------------------------------------
// Hooks
// -----------------------------------------------------------------------------
export const useErrorJian = () => {
  const [page, setPage] = useRecoilState(pageState);
  const [limit, setLimit] = useRecoilState(limitState);
  const [condition, setCondition] = useRecoilState(conditionState);
  const year = useSelectedOrCurrentYear();
  const teamId = useMe()?.teamId ?? '';
  const queryClient = useQueryClient();

  const { data, isFetching, refetch, fetchNextPage, hasNextPage } = useInfiniteQuery<
    components['schemas']['GetErroneousJianListResponse']
  >({
    queryKey: ['errorJians', { year, limit, teamId }],
    queryFn: async ({ pageParam = undefined }) => {
      const iYear = parseInt(year, 10);
      return await getErrorJians(
        teamId, iYear, limit,
        condition.monthFrom ?? undefined,
        condition.monthTo ?? undefined,
        condition.warningCount ?? undefined,
        pageParam,
      );
    },
    getNextPageParam: (lastPage) => (lastPage.nextToken || null),
  });

  const updateCondition = (cond: {
    monthFrom?: number | null;
    monthTo?: number | null;
    warningCount?: 0 | 1;
  }) => {
    setCondition((prev) => {
      return { ...prev, ...cond }
    })
  };

  const search = () => {
    if (isFetching) return;
    setPage(0);
    queryClient.removeQueries(['errorJians', { year }]);
    refetch();
  }

  const isLatestPage = data?.pages.length === page + 1;
  const isLastPage = isLatestPage && !hasNextPage;

  const incrementPage = useCallback(() => {
    if (isLastPage) return;
    if (isLatestPage) fetchNextPage();
    setPage(page + 1);
  }, [setPage, fetchNextPage, page, isLastPage, isLatestPage]);

  const decrementPage = useCallback(() => {
    setPage(page > 0 ? page - 1 : 0);
  }, [setPage, page]);

  const changeLimit = useCallback(
    (newLimit: number) => {
      setPage(0);
      setLimit(newLimit);
      queryClient.removeQueries(['errorJians', { year }]);
    },
    [fetchNextPage, setPage, setLimit],
  );

  return {
    errorJians: data?.pages.at(page),
    condition,
    page,
    limit,
    isLastPage,
    search,
    updateCondition,
    incrementPage,
    decrementPage,
    changeLimit,
  }
};